/**
 * Componente de configuración de alertas de gastos
 */

import React, { useState, useEffect } from 'react';
import { useUserSettings } from '../../../shared/hooks/useUserSettings';
import type { UserSettings } from '../../../shared/types';

export const AlertasSettings: React.FC = () => {
  const { settings, loading, updateSettings } = useUserSettings();
  const [umbralAumento, setUmbralAumento] = useState(20);
  const [umbralDisminucion, setUmbralDisminucion] = useState(20);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (settings) {
      setUmbralAumento(settings.alertas.umbralAumentoGasto);
      setUmbralDisminucion(settings.alertas.umbralDisminucionGasto);
    }
  }, [settings]);

  const handleSave = async () => {
    setSaving(true);
    setShowSuccess(false);

    try {
      await updateSettings({
        alertas: {
          umbralAumentoGasto: umbralAumento,
          umbralDisminucionGasto: umbralDisminucion,
          categoriasAMonitorear: settings?.alertas.categoriasAMonitorear || [],
        },
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Error al guardar configuración:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-blue-600 animate-spin"></i>
          <p className="ml-2 text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center gap-2 mb-6">
        <i className="ri-notification-badge-line text-2xl text-blue-600"></i>
        <h3 className="text-xl font-semibold text-gray-800">
          Configuración de Alertas de Gastos
        </h3>
      </div>

      <div className="space-y-6">
        {/* Información */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <i className="ri-information-line text-blue-600 text-xl mt-0.5"></i>
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">¿Cómo funcionan las alertas?</p>
              <p>
                El sistema te notificará cuando tus gastos en una categoría aumenten o disminuyan
                más del porcentaje que configures aquí, comparando el mes actual con el anterior.
              </p>
            </div>
          </div>
        </div>

        {/* Umbral de aumento */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Umbral de alerta por aumento
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={umbralAumento}
              onChange={(e) => setUmbralAumento(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="w-20 text-right">
              <span className="text-2xl font-bold text-red-600">{umbralAumento}%</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Te alertaremos cuando un gasto aumente más del {umbralAumento}% respecto al mes anterior
          </p>
          <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
            <p className="text-sm text-red-800">
              <i className="ri-alert-line mr-1"></i>
              <strong>Ejemplo:</strong> Si gastaste $10,000 en "Alimentación" el mes pasado y este
              mes gastas más de ${((10000 * (100 + umbralAumento)) / 100).toLocaleString('es-AR')},
              recibirás una alerta.
            </p>
          </div>
        </div>

        {/* Umbral de disminución */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Umbral de alerta por disminución
          </label>
          <div className="flex items-center gap-4">
            <input
              type="range"
              min="5"
              max="100"
              step="5"
              value={umbralDisminucion}
              onChange={(e) => setUmbralDisminucion(Number(e.target.value))}
              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
            />
            <div className="w-20 text-right">
              <span className="text-2xl font-bold text-green-600">{umbralDisminucion}%</span>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Te felicitaremos cuando un gasto disminuya más del {umbralDisminucion}% respecto al mes
            anterior
          </p>
          <div className="mt-3 bg-green-50 border border-green-200 rounded p-3">
            <p className="text-sm text-green-800">
              <i className="ri-checkbox-circle-line mr-1"></i>
              <strong>Ejemplo:</strong> Si gastaste $10,000 en "Entretenimiento" el mes pasado y
              este mes gastas menos de $
              {((10000 * (100 - umbralDisminucion)) / 100).toLocaleString('es-AR')}, recibirás
              una felicitación.
            </p>
          </div>
        </div>

        {/* Mensaje de éxito */}
        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-green-600 text-xl"></i>
            <p className="text-sm text-green-800 font-medium">
              Configuración guardada exitosamente
            </p>
          </div>
        )}

        {/* Botones */}
        <div className="flex gap-4 pt-4 border-t border-gray-200">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="ri-save-line"></i>
                Guardar Configuración
              </>
            )}
          </button>
          <button
            onClick={() => {
              setUmbralAumento(20);
              setUmbralDisminucion(20);
            }}
            className="bg-gray-100 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors font-medium"
          >
            Restaurar por defecto
          </button>
        </div>
      </div>
    </div>
  );
};
